/**
 * The side panel's DataViz tab (Adam, 11 Sep): every dashboard the customer
 * has, in the same three tiers the DataViz page uses — Company-level, My
 * DataViz, and one group per colleague — each row a tick box on the trees'
 * spacing. The TICK attaches the dashboard to the composer as a chip (so the
 * agent is told about it, by id, and reads it with `get_dashboard`); the NAME
 * opens a preview. Same split the Tables and Variables tabs make: looking
 * never commits to sending.
 *
 * Lists the whole customer, every job (#1162's "All DataViz"), because a
 * dashboard the user wants the agent to look at need not be on the session's
 * job — the chip carries the dashboard's own job, as table chips do.
 */
import React, { useMemo, useState } from 'react'
import { Box, Checkbox, CircularProgress, Collapse, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import useDashboards from '../../hooks/useDashboards'
import useAccount from '../../hooks/useAccount'
import { buildDashboardChip, type ComposerChip } from '../../hooks/useComposerChips'
import { dashboardTypeMeta } from '../../utils/dashboardType'
import type { Dashboard } from '../../types/gotypes'

export interface AgentDashboardsTabProps {
  customer: string
  /** Ids of the dashboards currently chipped, so the rows can mirror them. */
  selectedIds: Set<string>
  /** Add (on=true) or remove (on=false) a dashboard's chip. */
  onToggle: (chip: ComposerChip, on: boolean) => void
  /** Show the dashboard without attaching it. Clicking its NAME does this. */
  onPreview?: (chip: ComposerChip) => void
}

interface Tier {
  key: string
  label: string
  items: Dashboard[]
}

/** The three ownership tiers, exactly as the DataViz page groups them: a
 *  promoted dashboard is company-level whoever owns it; the rest split by
 *  owner into mine and one group per colleague. */
export function groupDashboards(dashboards: Dashboard[], userEmail: string): Tier[] {
  const company: Dashboard[] = []
  const mine: Dashboard[] = []
  const byColleague = new Map<string, Dashboard[]>()
  for (const d of dashboards) {
    if (d.promoted_at) company.push(d)
    else if (d.user === userEmail) mine.push(d)
    else byColleague.set(d.user, [...(byColleague.get(d.user) || []), d])
  }
  const byName = (a: Dashboard, b: Dashboard) => (a.config?.name || '').localeCompare(b.config?.name || '')
  const tiers: Tier[] = [
    { key: 'company', label: 'Company-level', items: company.sort(byName) },
    { key: 'mine', label: 'My DataViz', items: mine.sort(byName) },
    ...Array.from(byColleague.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([email, items]) => ({ key: `colleague:${email}`, label: email, items: items.sort(byName) })),
  ]
  return tiers.filter(t => t.items.length > 0)
}

export function chipForDashboard(d: Dashboard, tierLabel: string, jobLabel?: string): ComposerChip {
  const kind = dashboardTypeMeta(d.type).label
  return buildDashboardChip({
    id: d.id,
    name: d.config?.name || d.id,
    job: d.job || d.jobs?.[0] || '',
    jobLabel,
    description: `${kind} · ${tierLabel}${d.user ? ` · ${d.user}` : ''}`,
  })
}

export default function AgentDashboardsTab({ customer, selectedIds, onToggle, onPreview }: AgentDashboardsTabProps) {
  const account = useAccount()
  const userEmail = account.user?.session_info?.email || ''
  // Jobs are shown by display name, never by codename (Matt, 10 Sep).
  const jobLabels = useMemo(() => {
    const cust = account.user?.session_info?.sessionCusts?.find(c => c.name === customer)
    return new Map((cust?.sessionJobs || []).filter(j => j.name).map(j => [j.name as string, (j.displayName || j.name) as string]))
  }, [account.user, customer])
  const { dashboards, listIsLoading } = useDashboards({ customer, allJobs: true })
  const tiers = useMemo(() => groupDashboards(dashboards, userEmail), [dashboards, userEmail])
  const [closed, setClosed] = useState<Set<string>>(new Set())
  const toggleTier = (key: string) => setClosed(prev => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })

  return (
    <Box data-testid="agent-dashboards-tab" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1, overflow: 'auto' }}>
      {listIsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : tiers.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', px: 1 }}>
          No DataViz yet for this customer.
        </Typography>
      ) : tiers.map(tier => {
        const open = !closed.has(tier.key)
        return (
          <Box key={tier.key}>
            <Box
              role="button"
              aria-expanded={open}
              onClick={() => toggleTier(tier.key)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 22, py: '6px', px: 0.5, cursor: 'pointer', userSelect: 'none', borderRadius: '6px', '&:hover': { backgroundColor: 'action.hover' } }}
            >
              {open ? <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {tier.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.disabled', ml: 0.5 }}>{tier.items.length}</Typography>
            </Box>
            <Collapse in={open}>
              {tier.items.map(d => {
                const jobName = d.job || d.jobs?.[0] || ''
                const jobLabel = jobLabels.get(jobName) || jobName
                const chip = chipForDashboard(d, tier.label, jobLabels.get(jobName))
                const checked = selectedIds.has(chip.id)
                const meta = dashboardTypeMeta(d.type)
                return (
                  <Box
                    key={d.id}
                    data-testid={`dashboard-row-${d.id}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 22, py: '6px', pl: 3, pr: 1, borderRadius: '6px', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <Checkbox
                      checked={checked}
                      size="medium"
                      onChange={e => onToggle(chip, e.target.checked)}
                      sx={{ p: 0, flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 20 } }}
                      inputProps={{ 'aria-label': chip.label }}
                    />
                    <Box
                      onClick={() => onPreview?.(chip)}
                      title={chip.tooltip}
                      sx={{ flex: 1, minWidth: 0, cursor: onPreview ? 'pointer' : 'default' }}
                    >
                      <Typography sx={{ fontSize: 14, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {chip.label}
                      </Typography>
                      <Typography sx={{ fontSize: 11, lineHeight: 1.2, color: 'grey.500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.25 }}>
                        {meta.label}{jobLabel ? ` · ${jobLabel}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Collapse>
          </Box>
        )
      })}
    </Box>
  )
}
